import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { getDefaultEmailTemplates, getCompanyEmailTemplates, postCompanyEmailTemplate, postCompanyAssignmentEmailTemplate, updateCompanyEmailTemplateAsync, updateCompanyAssignmentEmailTemplateAsync } from '../apiClients/EmailTemplateClient';
import { getIdentityClientSettings, getSelectedClient } from '../utils/localStorage';
import { EmailTemplateEditorModel } from '../common/Interfaces';
import { AuthMethod, EmailTemplateTypeModel } from '../common/enums';
import { AppState } from '../redux/rootReducerTypes';
import useLocales from './useLocales';

export function useIdmEmailTemplates() {
    const { translate } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const user = useSelector((s: AppState) => s.userState.user);
    const selectedClient = getSelectedClient();
    const authMethod = getIdentityClientSettings().authMethod;
    const [templates, setTemplates] = useState<EmailTemplateEditorModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<number>(0);

    const load = useCallback(() => {
        if (!user) return;
        setLoading(true);
        const ctrl = new AbortController();

        Promise.all([
            getDefaultEmailTemplates(user.access_token, selectedClient.name, ctrl.signal),
            getCompanyEmailTemplates(user.access_token, selectedClient.name, ctrl.signal),
        ])
            .then(([defaults, company]) => {
                const mergedTemplates = defaults.map((defaultTemplate) => {
                    const companyTemplate = company.find((c) => c.templateType === defaultTemplate.templateType);
                    return {
                        id: companyTemplate?.id,
                        templateType: defaultTemplate.templateType,
                        enabled: companyTemplate?.enabled,
                        defaultTemplate: defaultTemplate.template,
                        template: companyTemplate?.template,
                        contentPlaceholderKeyValuesListDefault: defaultTemplate.contentPlaceholderKeyValues,
                        contentPlaceholderKeyValuesListCustom: companyTemplate?.contentPlaceholderKeyValues,
                        isCustom: !!companyTemplate,
                    } as EmailTemplateEditorModel;
                });
                setTemplates(mergedTemplates);
            })
            .catch(() =>
                enqueueSnackbar(translate('IdmEmailTemplate.LoadError'), { variant: 'error' })
            )
            .finally(() => {
                setLoading(false);
            });

        return () => void ctrl.abort();
    }, [user, selectedClient.name, enqueueSnackbar, translate]);

    useEffect(load, [load]);

    const handleOk = (editorTemplate: any) => {
        const isAssignment =
            authMethod === AuthMethod.IDM &&
            [
                EmailTemplateTypeModel.CustomerPortalInviteTemplate,
                EmailTemplateTypeModel.WebOfficeInviteTemplate,
                EmailTemplateTypeModel.UserBackendAssignmentAddedTemplate,
            ].includes(editorTemplate.templateType);

        if (!user) return;

        const call = editorTemplate.isCustom
            ? isAssignment
                ? updateCompanyAssignmentEmailTemplateAsync(editorTemplate, user.access_token, selectedClient.name)
                : updateCompanyEmailTemplateAsync(editorTemplate, user.access_token, selectedClient.name)
            : isAssignment
                ? postCompanyAssignmentEmailTemplate(editorTemplate, user.access_token, selectedClient.name)
                : postCompanyEmailTemplate(editorTemplate, user.access_token, selectedClient.name);

        call.then((result) => {
            if (result) {
                setTemplates((template) => [...template, editorTemplate]);
            } else {
                setTemplates((templates) => templates.map((temp) => temp.templateType === editorTemplate.templateType ? editorTemplate : temp));
            }
            enqueueSnackbar('Saved', { variant: 'success' });
        })
            .catch((err) => {
                console.error(err);
                enqueueSnackbar('Failed to save', { variant: 'error' });
            });

        setOpen(false);
    };

    const handleCancel = () => setOpen(false);

    const handleShowEdit = (type: number) => {
        setSelectedTemplate(type);
        setOpen(true);
    };

    return {
        templates,
        loading,
        open,
        selectedTemplate,
        handleOk,
        handleCancel,
        handleShowEdit,
    };
}